import { useRouter } from 'next/router';
import { parseAsStringEnum, useQueryState } from 'nuqs';

import {
  QuizsetFilterCriteriaSearchType,
  QuizsetSearchReqOrder,
  QuizsetSearchReqPeriod,
} from '@/api';
import { useCountryCode } from '@/shared/hooks';

export const PUBLIC_QUIZ_SEARCH_TYPE_TITLE_CONTENT = 'TITLE_CONTENT' as const;

export type PublicQuizSearchType =
  | QuizsetFilterCriteriaSearchType
  | typeof PUBLIC_QUIZ_SEARCH_TYPE_TITLE_CONTENT;

const PUBLIC_QUIZ_SEARCH_TYPES = [
  ...Object.values(QuizsetFilterCriteriaSearchType),
  PUBLIC_QUIZ_SEARCH_TYPE_TITLE_CONTENT,
] as PublicQuizSearchType[];

export const isTitleAutocompleteSearchType = (
  searchType: PublicQuizSearchType,
) =>
  searchType === QuizsetFilterCriteriaSearchType.ALL ||
  searchType === QuizsetFilterCriteriaSearchType.TITLE ||
  searchType === PUBLIC_QUIZ_SEARCH_TYPE_TITLE_CONTENT;

export const useSearchQuizQuery = () => {
  const { pathname } = useRouter();
  const { countryCode } = useCountryCode();

  const [searchText, setSearchText] = useQueryState('q', {
    defaultValue: '',
  });

  const [searchType, setSearchType] = useQueryState(
    'qType',
    parseAsStringEnum<PublicQuizSearchType>(
      PUBLIC_QUIZ_SEARCH_TYPES,
    ).withDefault(QuizsetFilterCriteriaSearchType.ALL),
  );

  const getDefaultOrder = () => {
    const recentlyOrderedPaths = ['profile', 'myquiz'];
    return recentlyOrderedPaths.some(path => pathname.includes(path))
      ? QuizsetSearchReqOrder.RECENTLY
      : QuizsetSearchReqOrder.POPULARITY;
  };

  const getDefaultPeriod = () => {
    // KR, ID, TH, MY: 1주일 기본값
    // 그 외 국가: 전체 기본값
    const weeklyCountries = ['KR', 'ID', 'TH', 'MY'];
    return weeklyCountries.includes(countryCode)
      ? QuizsetSearchReqPeriod.WEEK
      : QuizsetSearchReqPeriod.ALL;
  };

  const [order, setOrder] = useQueryState(
    'order',
    parseAsStringEnum<QuizsetSearchReqOrder>(
      Object.values(QuizsetSearchReqOrder),
    ).withDefault(getDefaultOrder()),
  );

  const [period, setPeriod] = useQueryState(
    'period',
    parseAsStringEnum<QuizsetSearchReqPeriod>(
      Object.values(QuizsetSearchReqPeriod),
    ).withDefault(getDefaultPeriod()),
  );

  const handleSearchText = (text: string) => {
    setSearchText(text, {
      history: 'replace',
      shallow: true,
    });
  };

  const handleOrder = (order: QuizsetSearchReqOrder) => {
    setOrder(order, {
      history: 'replace',
      shallow: true,
    });
  };

  const handlePeriod = (period: QuizsetSearchReqPeriod) => {
    setPeriod(period, {
      history: 'replace',
      shallow: true,
    });
  };

  const handleSearchType = (type: PublicQuizSearchType) => {
    setSearchType(type, {
      history: 'replace',
      shallow: true,
    });
  };

  return {
    searchText,
    searchType,
    order,
    period,
    handleSearchText,
    handleOrder,
    handlePeriod,
    handleSearchType,
  };
};
